import * as React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import useAuthenticatedApi from '../../Authentication/components/hooks/useAuthenticatedApi';
import EventShell from '../chrome/EventShell';
import useEventChrome, { LIVE_WINDOW_MS } from '../chrome/useEventChrome';

/*
 * Event Overview — the subject|details archetype's first instantiation
 * (docs/design-brief/event-overview-concept.html v7). One stable
 * layout: vitals (stat tiles + activity graph) over production details
 * and devices. Sections whose backend doesn't exist yet (client
 * logistics review, call sheet, tasks — PLAN.md backend track) join
 * when their tables land; the mode control likewise waits on rule 5b.
 * The chart is by DAY (mediaStats/shareStats granularity today); the
 * by-hour view for single-day events is a backend enrichment.
 */

interface DayPoint {
    day: string;
    captures: number;
    shares: number;
}

function fmtDay(mmddyyyy: string): string {
    const [m, d] = mmddyyyy.split('/');
    const date = new Date(Number(mmddyyyy.slice(6)), Number(m) - 1, Number(d));
    return date.toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
    });
}

function agoLabel(iso: string): string {
    const ms = Date.now() - new Date(iso).getTime();
    if (ms < 90 * 1000) return `${Math.max(1, Math.round(ms / 1000))}s ago`;
    if (ms < 90 * 60 * 1000) return `${Math.round(ms / 60000)}m ago`;
    return `${Math.round(ms / 3600000)}h ago`;
}

function seenLabel(iso?: string): string {
    if (!iso) return 'never seen';
    const t = new Date(iso).getTime();
    if (Number.isNaN(t)) return 'never seen';
    const ms = Date.now() - t;
    if (ms < 90 * 60 * 1000) return `seen ${Math.max(1, Math.round(ms / 60000))} min ago`;
    if (ms < 24 * 3600 * 1000) return `seen ${Math.round(ms / 3600000)} h ago`;
    return `seen ${new Date(t).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}`;
}

function Chart({ points }: { points: DayPoint[] }) {
    const W = 600;
    const H = 110;
    const BASE = 104;
    const max = Math.max(1, ...points.map((p) => Math.max(p.captures, p.shares)));
    const x = (i: number) =>
        points.length === 1 ? W / 2 : (i / (points.length - 1)) * W;
    const y = (v: number) => BASE - (v / max) * 92;
    const line = (pick: (p: DayPoint) => number) =>
        points.map((p, i) => `${x(i)},${y(pick(p))}`).join(' ');
    return (
        <>
            <svg
                viewBox={`0 0 ${W} ${H}`}
                preserveAspectRatio="none"
                aria-label="Captures and shares by day"
            >
                <line
                    x1="0"
                    y1={BASE}
                    x2={W}
                    y2={BASE}
                    stroke="color-mix(in oklab, var(--base) 8%, transparent)"
                    strokeWidth="1"
                />
                <polyline
                    fill="none"
                    stroke="color-mix(in oklab, var(--base) 74%, transparent)"
                    strokeWidth="1.5"
                    points={line((p) => p.captures)}
                />
                <polyline
                    fill="none"
                    stroke="var(--live)"
                    strokeWidth="1.5"
                    points={line((p) => p.shares)}
                />
            </svg>
            <div className="g-x">
                {points.map((p, i) =>
                    points.length <= 8 ||
                    i === 0 ||
                    i === points.length - 1 ||
                    i % Math.ceil(points.length / 6) === 0 ? (
                        <span key={p.day}>{fmtDay(p.day)}</span>
                    ) : (
                        <span key={p.day} />
                    ),
                )}
            </div>
        </>
    );
}

export default function OverviewV3() {
    const { activationId } = useParams();
    const id = Number(activationId);
    const chrome = useEventChrome(id);
    const { photoStats, live, lastCaptureAt } = chrome;
    const authenticatedApi = useAuthenticatedApi();

    const [mediaDays, setMediaDays] = useState<any[] | null>(null);
    const [shareDays, setShareDays] = useState<any[] | null>(null);
    const [devices, setDevices] = useState<any[] | null>(null);
    const [production, setProduction] = useState<any>(null);

    useEffect(() => {
        if (!id) return;
        let alive = true;
        const guard =
            <T,>(set: (v: T) => void) =>
            (v: T) => {
                if (alive) set(v);
            };
        authenticatedApi
            .get(`/activations/${id}/mediaStats`)
            .then((r: any) => guard(setMediaDays)(r.data ?? []))
            .catch(() => guard(setMediaDays)([]));
        authenticatedApi
            .get(`/activations/${id}/shareStats`)
            .then((r: any) => guard(setShareDays)(r.data ?? []))
            .catch(() => guard(setShareDays)([]));
        authenticatedApi
            .get(`/activations/${id}/devices`)
            .then((r: any) => guard(setDevices)(r.data ?? []))
            .catch(() => guard(setDevices)([]));
        authenticatedApi
            .get(`/production/activations/${id}`)
            .then((r: any) => guard(setProduction)(r.data))
            .catch(() => {});
        return () => {
            alive = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const captures = Number(photoStats?.photoCount ?? 0);
    const shares = useMemo(
        () =>
            (shareDays ?? []).reduce(
                (sum, r) => sum + Number(r.total ?? 0),
                0,
            ),
        [shareDays],
    );

    const points = useMemo<DayPoint[]>(() => {
        const byDay = new Map<string, DayPoint>();
        (mediaDays ?? []).forEach((r) => {
            const day = r.uploadDate;
            if (!day) return;
            byDay.set(day, {
                day,
                captures: Number(r.total ?? 0),
                shares: 0,
            });
        });
        (shareDays ?? []).forEach((r) => {
            const day = r.shareDate;
            if (!day) return;
            const entry = byDay.get(day) ?? { day, captures: 0, shares: 0 };
            entry.shares = Number(r.total ?? 0);
            byDay.set(day, entry);
        });
        return Array.from(byDay.values()).sort((a, b) => {
            const key = (d: string) =>
                `${d.slice(6)}-${d.slice(0, 2)}-${d.slice(3, 5)}`;
            return key(a.day) < key(b.day) ? -1 : 1;
        });
    }, [mediaDays, shareDays]);

    const staff: any[] = production?.staffAssignments ?? [];
    const gear: any[] = production?.gearAssignments ?? [];
    const contacts: any[] = production?.contacts ?? [];

    const schedule = [production?.startAt, production?.endAt]
        .filter(Boolean)
        .map((iso: string) =>
            new Date(iso).toLocaleTimeString(undefined, {
                hour: 'numeric',
                minute: '2-digit',
            }),
        )
        .join(' – ');

    const personName = (a: any) =>
        a.name ??
        a.staffName ??
        [a.firstName, a.lastName].filter(Boolean).join(' ') ??
        '';

    return (
        <EventShell
            orgInitials={chrome.orgInitials}
            orgLogo={chrome.orgLogo}
            eventName={chrome.eventName}
            eventSub={chrome.eventSub}
            live={live}
            quickLinks={chrome.quickLinks}
            nav={chrome.nav}
            activeSection="overview"
        >
            <div className="dets">
                <div className="vitals">
                    <div style={{ flex: 1 }}>
                        <p className="s-lab">Activity</p>
                        {live && lastCaptureAt && (
                            <p className="s-live">
                                <span className="dot pulse" />
                                Live · last capture {agoLabel(lastCaptureAt)}
                            </p>
                        )}
                        <div className="tiles">
                            <div className="tile">
                                <p className="tl">Captures</p>
                                <div className="tn">
                                    {photoStats
                                        ? captures.toLocaleString()
                                        : '—'}
                                </div>
                            </div>
                            <div className="tile">
                                <p className="tl">Shares</p>
                                <div className="tn">
                                    {shareDays
                                        ? shares.toLocaleString()
                                        : '—'}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="graph">
                    <div className="g-h">
                        <span className="g-leg">
                            <span
                                className="sw"
                                style={{ background: 'var(--text-secondary)' }}
                            />
                            Captures
                        </span>
                        <span className="g-leg">
                            <span
                                className="sw"
                                style={{ background: 'var(--live)' }}
                            />
                            Shares
                        </span>
                        <span className="g-when">by day</span>
                    </div>
                    {points.length > 0 ? (
                        <Chart points={points} />
                    ) : (
                        <p className="g-empty">
                            No captures yet — the chart begins with the first
                            one.
                        </p>
                    )}
                </div>

                <div className="stack">
                    <div className="sec">
                        <div className="sec-h">
                            <h2>Production details</h2>
                            {/* name, date, location; crew, gear and the rest live in Production */}
                            <Link className="link" to={`/activations/${id}/edit`} style={{ marginLeft: 'auto', fontSize: 'var(--fs-sm)' }}>
                                Edit details
                            </Link>
                            <Link className="link" to={`/activations/${id}/production`} style={{ marginLeft: 12, fontSize: 'var(--fs-sm)' }}>
                                Edit production
                            </Link>
                        </div>
                        {production ? (
                            <>
                                {schedule && (
                                    <div className="frow">
                                        <span className="fl">Schedule</span>
                                        <span className="fv">{schedule}</span>
                                    </div>
                                )}
                                {production.setupLocation && (
                                    <div className="frow">
                                        <span className="fl">Setup</span>
                                        <span className="fv">
                                            {production.setupLocation}
                                        </span>
                                    </div>
                                )}
                                {production.boothType && (
                                    <div className="frow">
                                        <span className="fl">Booth</span>
                                        <span className="fv">
                                            {production.boothType}
                                            {production.printing
                                                ? ` · ${production.printing}`
                                                : ''}
                                        </span>
                                    </div>
                                )}
                                {staff.length > 0 && (
                                    <div className="frow">
                                        <span className="fl">Staff</span>
                                        <span className="fv pchips">
                                            {staff.map((a) => (
                                                <span
                                                    className="pchip"
                                                    key={a.id}
                                                >
                                                    {personName(a)}
                                                    {a.role
                                                        ? ` · ${a.role}`
                                                        : ''}
                                                </span>
                                            ))}
                                        </span>
                                    </div>
                                )}
                                {gear.length > 0 && (
                                    <div className="frow">
                                        <span className="fl">Gear</span>
                                        <span className="fv pchips">
                                            {gear.map((g) => (
                                                <span
                                                    className="pchip"
                                                    key={g.id}
                                                >
                                                    {g.assetName ?? g.name ?? g.gearName ?? ''}
                                                </span>
                                            ))}
                                        </span>
                                    </div>
                                )}
                                {contacts.length > 0 && (
                                    <div className="frow">
                                        <span className="fl">Contacts</span>
                                        <span className="fv pchips">
                                            {contacts.map((c) => (
                                                <span
                                                    className="pchip"
                                                    key={c.activationContactId ?? `${c.id}-${c.role}`}
                                                >
                                                    {personName(c)}
                                                </span>
                                            ))}
                                        </span>
                                    </div>
                                )}
                                {production.notes && (
                                    <div className="frow">
                                        <span className="fl">Notes</span>
                                        <span className="fv">
                                            {production.notes}
                                        </span>
                                    </div>
                                )}
                            </>
                        ) : (
                            <p className="quiet-empty">
                                No production record for this event.
                            </p>
                        )}
                    </div>

                    <div className="sec">
                        <div className="sec-h">
                            <h2>Devices on this event</h2>
                            <span className="n">
                                {devices
                                    ? `${devices.length} assigned`
                                    : ''}
                            </span>
                        </div>
                        {devices && devices.length === 0 && (
                            <p className="quiet-empty">
                                No devices assigned.
                            </p>
                        )}
                        {(devices ?? []).map((d) => {
                            const seen = d.lastUploadAt;
                            const online =
                                !!seen &&
                                Date.now() - new Date(seen).getTime() <
                                    LIVE_WINDOW_MS;
                            return (
                                <div className="trow2" key={d.id}>
                                    <span
                                        className={
                                            online ? 'pulse' : 'pulse off'
                                        }
                                    />
                                    <span className="nm2">
                                        {d.friendlyName ||
                                            d.deviceName ||
                                            d.deviceKeyId}
                                    </span>
                                    <span className="right">
                                        {seenLabel(seen)}
                                    </span>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
        </EventShell>
    );
}
